import { PriceUtils, ProductChoice, hasDiscount } from "@repo/common"
import { cn } from "@repo/ui/lib/utils"
import * as React from "react"
import { useTranslations } from "use-intl"
import { OriginalPrice } from "../../components/original-price"
import { ProductOrderingIndicator } from "./product-ordering-indicator"
import {
  SelectableCard,
  SelectableCardBadge,
  SelectableCardContent,
  SelectableCardDescription,
  SelectableCardHeader,
  SelectableCardIndicator,
  SelectableCardLabel,
  SelectableCardSection,
  SelectableCardText,
} from "./selectable-card"

interface ServiceProposalManualActionProductChoicesFieldProps {
  productChoices: ProductChoice[]
  onProductSelect?: (productId: string, choiceId: string) => void
  shouldDimContent?: boolean
  className?: string
  productDecisions?: Record<string, string>
  interactive: boolean
  showComments?: boolean
}

const ServiceProposalManualActionProductChoicesField = React.forwardRef<
  HTMLDivElement,
  ServiceProposalManualActionProductChoicesFieldProps
>(
  (
    {
      className,
      productChoices,
      onProductSelect,
      shouldDimContent = false,
      productDecisions = {},
      interactive = true,
      showComments = true,
    },
    ref,
  ) => {
    const t = useTranslations("ui")
    if (productChoices.length === 0) return null

    return (
      <SelectableCardSection
        ref={ref}
        className={cn(shouldDimContent ? "opacity-40" : "", className)}
      >
        {productChoices.map((choiceGroup: ProductChoice, index) => {
          const allProducts = [
            choiceGroup.recommendedAlternative,
            ...choiceGroup.otherAlternatives,
          ]
          return (
            <div key={choiceGroup.id} className="space-y-2">
              <SelectableCardLabel>
                {productChoices.length === 1
                  ? t("proposalForm.productChoice")
                  : t("proposalForm.productChoiceNumber", {
                      number: index + 1,
                    })}
              </SelectableCardLabel>
              {showComments && choiceGroup.comment && (
                <SelectableCardDescription>
                  {choiceGroup.comment}
                </SelectableCardDescription>
              )}
              {allProducts
                .sort((a, b) => Number(a.price) - Number(b.price))
                .map((product) => {
                  const selected =
                    productDecisions[choiceGroup.id] === product.id
                  const activeDiscount = hasDiscount(product.discount)
                    ? product.discount
                    : null
                  const discountedPrice = activeDiscount
                    ? PriceUtils.applyDiscount(
                        product.price,
                        product.discount ?? null,
                      )
                    : product.price

                  return (
                    <SelectableCard
                      key={product.id}
                      selected={selected}
                      onSelect={() =>
                        onProductSelect?.(product.id, choiceGroup.id)
                      }
                      variant="radio"
                      color="secondary"
                      interactive={!shouldDimContent && interactive}
                      className={cn({
                        "bg-background": selected,
                      })}
                    >
                      <SelectableCardHeader>
                        <SelectableCardText>{product.title}</SelectableCardText>
                        <SelectableCardIndicator />
                      </SelectableCardHeader>

                      <SelectableCardContent>
                        {activeDiscount ? (
                          <div className="flex items-center gap-2">
                            <OriginalPrice>
                              {PriceUtils.format(product.price)}
                            </OriginalPrice>
                            <SelectableCardLabel className="text-destructive-foreground">
                              {PriceUtils.format(discountedPrice)}
                            </SelectableCardLabel>
                            <span className="text-xs text-destructive-foreground">
                              {PriceUtils.formatDiscountLabel(activeDiscount)}
                            </span>
                          </div>
                        ) : (
                          <SelectableCardLabel>
                            {PriceUtils.format(product.price)}
                          </SelectableCardLabel>
                        )}

                        {product.id ===
                          choiceGroup.recommendedAlternative.id && (
                          <SelectableCardBadge>
                            {t("readOnlyServiceProposal.recommended")}
                          </SelectableCardBadge>
                        )}
                        {product.needsOrdering && (
                          <ProductOrderingIndicator
                            expectedDeliveryDate={product.expectedDeliveryDate}
                          />
                        )}
                      </SelectableCardContent>
                    </SelectableCard>
                  )
                })}
            </div>
          )
        })}
      </SelectableCardSection>
    )
  },
)

ServiceProposalManualActionProductChoicesField.displayName =
  "ServiceProposalManualActionProductChoicesField"

export {
  ServiceProposalManualActionProductChoicesField,
  type ServiceProposalManualActionProductChoicesFieldProps,
}
